import React, { useEffect } from "react";
import LoginType from "../../model/LoginType";
import StorageKey from "../../model/StorageKey";
import { useHistory } from "react-router-dom";
import { getAnalytics, logEvent } from "firebase/analytics";
import Swal from "sweetalert2";
import "./login.css";

const bgImg = process.env.PUBLIC_URL + '/assets/images/game/bg.jpg';
const btnMetaMaskImg = process.env.PUBLIC_URL + '/assets/images/game/btn_metamask.png';
const btnKubWalletImg = process.env.PUBLIC_URL + '/assets/images/game/btn_kubwallet.png';
const btnBitKubNextImg = process.env.PUBLIC_URL + '/assets/images/game/btn_bitkubnext_classic.png';
const logoImg = process.env.PUBLIC_URL + '/assets/images/game/Logo.png';
const SHOW_KUB_WALLET_GAME_THEME_TOGGLE = false;

export const clearApplicationAuthStorage = () => {
    localStorage.removeItem(StorageKey.LONGIN_TYPE);
    localStorage.removeItem(StorageKey.BITKUB_NEXT_AUTH);
    localStorage.removeItem(StorageKey.BITKUB_NEXT_USER_INFO);
};

function LoginScreen() {
    const history = useHistory();

    useEffect(() => {
        clearApplicationAuthStorage();
    }, []);

    const getBitkubNextOAuth2URL = () => {
        const url = new URL(process.env.REACT_APP_BITKUB_NEXT_ACCOUNT_ENDPOINT + "/oauth2/authorize");
        url.searchParams.append("response_type", "code");
        url.searchParams.append("client_id", process.env.REACT_APP_BITKUB_NEXT_CLIENT_ID);
        url.searchParams.append("redirect_uri", window.location.protocol + "//" + window.location.host + "/oauth/callback");
        return url;
    };

    const saveLoginType = (loginType) => {
        localStorage.setItem(StorageKey.LONGIN_TYPE, loginType);
    };

    const logEventLogin = (loginType) => {
        try {
            const analytics = getAnalytics();
            if (analytics) {
                logEvent(analytics, 'login', { login_type: loginType });
            }
        } catch (e) {
            console.error("Failed to log login event", e);
        }
    };

    const onMetamaskClicked = async () => {
        saveLoginType(LoginType.METAMASK);
        logEventLogin(LoginType.METAMASK);
        history.replace("/");
    };

    const onKubWalletClicked = async () => {
        saveLoginType(LoginType.KUB_WALLET);
        logEventLogin(LoginType.KUB_WALLET);
        history.replace("/");
    };

    const onKubWalletRequested = async () => {
        try {
            if (localStorage.getItem(StorageKey.HIDE_KUB_WALLET_WARNING) === "true") {
                return onKubWalletClicked();
            }
        } catch (e) {
            // Still show the warning if browser storage is unavailable.
        }

        const result = await Swal.fire({
            title: '<span class="kub-wallet-warning-heading-icon" aria-hidden="true">⚠️</span> Attention',
            html: `
                ${SHOW_KUB_WALLET_GAME_THEME_TOGGLE ? `
                    <button type="button" class="kub-wallet-theme-toggle" aria-pressed="false">
                        🎨 Game theme
                    </button>
                ` : ""}
                <div class="kub-wallet-warning-divider" aria-hidden="true"><span>◇</span></div>
                <div class="kub-wallet-warning-message" lang="th">
                    <p class="kub-wallet-warning-intro">
                        คุณกำลังจะ Login เกมด้วย <strong class="kub-wallet-warning-wallet">KUB Wallet</strong><br>
                        ซึ่งเป็นบัญชีใหม่และเริ่มต้นเกมใหม่
                    </p>
                    <p>
                        หากคุณเป็นผู้เล่นเดิมที่ต้องการเล่นเกมด้วยบัญชีเดิม<br>
                        ที่มีข้อมูลบัญชีและ Item ให้เลือกเข้าใช้งานด้วยปุ่ม<br>
                        <strong class="kub-wallet-warning-classic">Bitkub NEXT (Classic Wallet)</strong>
                    </p>
                </div>
                <label class="kub-wallet-warning-preference" lang="th">
                    <input type="checkbox" id="kub-wallet-hide-warning">
                    <span>ไม่ต้องแสดงข้อความเตือนนี้อีก</span>
                </label>
            `,
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonText: 'เข้าเล่นด้วย Address ใหม่<span>(โปรดทบทวนข้อความเตือนก่อน)</span>',
            cancelButtonText: "ย้อนกลับไปเลือกบัญชีอีกครั้ง",
            cancelButtonColor: "#495057",
            closeButtonAriaLabel: "ปิดข้อความเตือน",
            reverseButtons: true,
            focusCancel: true,
            buttonsStyling: true,
            width: 660,
            customClass: {
                popup: "kub-wallet-warning",
                title: "kub-wallet-warning-title",
                htmlContainer: "kub-wallet-warning-content",
                actions: "kub-wallet-warning-actions",
                confirmButton: "kub-wallet-warning-confirm",
                cancelButton: "kub-wallet-warning-cancel",
                closeButton: "kub-wallet-warning-close",
            },
            didOpen: (popup) => {
                const themeToggle = popup.querySelector(".kub-wallet-theme-toggle");
                if (themeToggle) {
                    themeToggle.addEventListener("click", (event) => {
                        const useGameTheme = document.body.classList.toggle("kub-wallet-warning-game-theme");
                        event.currentTarget.setAttribute("aria-pressed", String(useGameTheme));
                        event.currentTarget.textContent = useGameTheme ? "🎨 Default theme" : "🎨 Game theme";
                    });
                }
                popup.querySelector("#kub-wallet-hide-warning").addEventListener("change", (event) => {
                    try {
                        if (event.target.checked) {
                            localStorage.setItem(StorageKey.HIDE_KUB_WALLET_WARNING, "true");
                        } else {
                            localStorage.removeItem(StorageKey.HIDE_KUB_WALLET_WARNING);
                        }
                    } catch (e) {
                        // Storage restrictions must not prevent the player from continuing.
                    }
                });
            },
            didClose: () => document.body.classList.remove("kub-wallet-warning-game-theme"),
        });

        if (result.isConfirmed) {
            return onKubWalletClicked();
        }
    };

    const onBitkubNextClicked = async () => {
        saveLoginType(LoginType.BITKUB_NEXT);
        logEventLogin(LoginType.BITKUB_NEXT);
        window.open(getBitkubNextOAuth2URL(), "_self");
    };

    return (
        <div className="login-container" style={{ backgroundImage: `url(${bgImg})` }}>
            <img src={logoImg} width="30%" alt="Morning Moon Village Logo" />
            <input
                type="image"
                src={btnMetaMaskImg}
                onClick={onMetamaskClicked}
                style={{ width: 430, marginBottom: 12 }}
                alt="Connect with MetaMask"
            />
            <input
                type="image"
                src={btnBitKubNextImg}
                onClick={onBitkubNextClicked}
                style={{ width: 430 }}
                alt="Connect with Bitkub Next"
            />
            <input
                type="image"
                src={btnKubWalletImg}
                onClick={onKubWalletRequested}
                style={{ width: 430, marginBottom: 12 }}
                alt="Connect with KUB Wallet"
            />
            <div id="license-login">
                <div className="license">
                    <footer>
                        <p>©Copyright 2021 Extend Interactive Co., Ltd. All right reserved.</p>
                    </footer>
                </div>
            </div>
        </div>
    );
}

export default LoginScreen;
